import * as React from 'react'
import { styled, Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { useTranslation } from 'react-i18next'
import { Typography, Button } from '@mui/material'
import { ReactComponent as SettingIcon } from '../icon/map_layer_setting.svg'
import { ReactComponent as DeleteIcon } from '../icon/map_layer_delete.svg'

type CustomLayerCardProps = {
    item: MapCustomLayer
    displayDeleteButton: boolean
    onSettingExternalMapUrl: (item: MapCustomLayer) => void
    onDeleteExternalMapUrl?: (item: MapCustomLayer) => void
}

const CustomLayerCard = (props: CustomLayerCardProps) => {
    const { item, displayDeleteButton, onSettingExternalMapUrl, onDeleteExternalMapUrl } = props
    const { t } = useTranslation()
    const classes = useStyles()


    let type: string = ''
    switch (item.type) {
        case 'wms':
            type = t('LABEL.MAP.MAP_LAYER.LAYER_TYPE_WMS')
            break
        case 'wmts':
            type = t('LABEL.MAP.MAP_LAYER.LAYER_TYPE_WMTS')
            break
        case 'dynamic':
        case 'tile':
        case 'layer':
            type = t('LABEL.MAP.MAP_LAYER.LAYER_TYPE_LAYER')
            break
        default:
            break
    }

    const labelWidth: number = displayDeleteButton ? 180 : 240
    return (
        <StyledContainerMapLayerItem>
            <StyledDisplayDivItem style={{ width: labelWidth }}>
                <div>
                    <StyledTypoUrl className={classes.lineClamp}>{item.name}</StyledTypoUrl>
                </div>
                <div>
                    <StyledTypoType className={classes.lineClamp}>{type}</StyledTypoType>
                </div>
            </StyledDisplayDivItem>
            <StyledDisplayDivButtonGroup>
                <StyledDisplayDivItem>
                    <StyledButtonSetting
                        onClick={() => {
                            onSettingExternalMapUrl(item)
                        }}
                        classes={{ root: classes.buttonSetting }}
                    >
                        <IconSetting />
                        {t('BUTTON.SETTING')}
                    </StyledButtonSetting>
                </StyledDisplayDivItem>
                {displayDeleteButton ? (
                    <StyledDisplayDivButtonDelete>
                        <StyledButtonDelete
                            onClick={() => {
                                onDeleteExternalMapUrl?.(item)
                            }}
                            classes={{ root: classes.deleteSetting }}
                        >
                            <IconDelete />
                            {t('BUTTON.DELETE')}
                        </StyledButtonDelete>
                    </StyledDisplayDivButtonDelete>
                ) : null}
            </StyledDisplayDivButtonGroup>
        </StyledContainerMapLayerItem>
    )
}

const useStyles = makeStyles((theme: Theme) => ({
    buttonSetting: {
        minWidth: '50px !important',
        padding: '0px !important',
    },
    deleteSetting: {
        minWidth: '35px !important',
        padding: '0px !important',
    },
    lineClamp: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
}))

const StyledContainerMapLayerItem = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px',
    marginBottom: '10px',
    backgroundColor: '#f0f7ff',
    border: `1px solid ${theme.palette.text.disabled}`,
    borderRadius: '3px',
}))

const StyledDisplayDivItem = styled('div')(({ theme }) => ({
    display: 'inline-block',
}))

const StyledDisplayDivButtonGroup = styled('div')(({ theme }) => ({
    display: 'inline-block',
    position: 'relative',
    top: 10,
}))

const StyledDisplayDivButtonDelete = styled('div')(({ theme }) => ({
    display: 'inline-block',
    borderLeft: '1px solid #C9C9C9',
    marginLeft: 5,
    paddingLeft: 5,
}))

const StyledTypoUrl = styled(Typography)(({ theme }) => ({
    fontSize: '14px',
    color: theme.palette.secondary.dark,
}))

const StyledTypoType = styled(Typography)(({ theme }) => ({
    fontSize: '12px',
    color: '#9A9A9A',
}))

const StyledButtonSetting = styled(Button)(({ theme }) => ({
    height: '100%',
    width: '100%',
    padding: '0px',
    color: theme.palette.primary.dark,
    [theme.breakpoints.down('sm')]: {
        padding: 0,
        fontSize: '10px',
    },
}))

const StyledButtonDelete = styled(Button)(({ theme }) => ({
    height: '100%',
    width: '100%',
    padding: '0px',
    color: theme.palette.getContrastText('required'),
    [theme.breakpoints.down('sm')]: {
        padding: 0,
        fontSize: '10px',
    },
}))

const IconSetting = styled(SettingIcon)(({ theme }) => ({
    height: '13px',
    marginRight: '2px',
}))

const IconDelete = styled(DeleteIcon)(({ theme }) => ({
    height: '13px',
    marginRight: '2px',
}))

export default CustomLayerCard
